import { lowerCamelCase } from "../../utils/helpers";

export default function moduleFactory(Module, selector, options = {}) {
  const elements = Array.from(document.querySelectorAll(selector));

  return elements.map((element) => new Module(element, options));
}

export function destroyModulesWithin(container, removeOrphanedNodes = false) {
  const destroyedInstances = {};

  Object.keys(window.ODS.instances).forEach((moduleName) => {
    window.ODS.instances[moduleName].forEach((instance) => {
      if (typeof destroyedInstances[moduleName] !== "object") {
        destroyedInstances[moduleName] = [];
      }

      let isInContainer = container.contains(instance.element);
      if (moduleName === "modal") {
        const openers = instance.instance?._openers || [];
        isInContainer =
          openers.length > 0 &&
          openers.every((opener) => container.contains(opener));
      } else if (moduleName === "tooltip") {
        isInContainer = container.contains(instance.trigger);
      }

      if (isInContainer) {
        instance.destroy();
        destroyedInstances[moduleName].push(instance);
      }

      if (
        isInContainer &&
        removeOrphanedNodes &&
        ["modal", "tooltip"].includes(moduleName)
      ) {
        instance.element.remove();
      }
    });

    window.ODS.instances[moduleName] = window.ODS.instances[moduleName].filter(
      (instance) => !destroyedInstances[moduleName].includes(instance),
    );
  });

  return destroyedInstances;
}

export function initModulesWithin(container) {
  const instances = {};
  window.ODS.moduleSelectorPairs.forEach((module) => {
    const name = lowerCamelCase(module.name);
    let elements;

    if (name === "modal" && container.getAttribute?.("id") !== "root-modals") {
      const modalsRoot = document.querySelector("#root-modals");
      elements = modalsRoot
        ? Array.from(modalsRoot.querySelectorAll(module.selector)).filter(
            (modal) =>
              Array.from(
                container.querySelectorAll("[data-a11y-dialog-show]"),
              ).some(
                (opener) =>
                  opener.getAttribute("data-a11y-dialog-show") ===
                  modal.getAttribute("id"),
              ),
          )
        : Array.from(container.querySelectorAll(module.selector));
    } else if (
      name === "tooltip" &&
      container.getAttribute?.("id") !== "root-tooltips"
    ) {
      const tooltipsRoot = document.querySelector("#root-tooltips");
      elements = tooltipsRoot
        ? Array.from(tooltipsRoot.querySelectorAll(module.selector)).filter(
            (tooltip) =>
              Array.from(
                container.querySelectorAll(
                  '[data-tooltip-trigger="true"][aria-describedby]',
                ),
              ).some(
                (trigger) =>
                  trigger.getAttribute("aria-describedby") ===
                  tooltip.getAttribute("id"),
              ),
          )
        : Array.from(container.querySelectorAll(module.selector));
    } else {
      elements = Array.from(container.querySelectorAll(module.selector));
    }

    // Filter out elements that already have an instance (e.g., initialized by React)
    const instancePropertyName = `ODS_${module.name}`;
    const uninitializedElements = elements.filter(
      (element) => !element[instancePropertyName],
    );

    if (window && window.ODS && window.ODS.instances[name]) {
      // Also clean up instances from elements that are no longer in the document
      window.ODS.instances[name] = window.ODS.instances[name].filter(
        (instance) => {
          const isInDocument = document.contains(instance.element);
          if (!isInDocument) {
            // Clean up the instance if its element is no longer in the document
            instance.destroy?.();
          }
          return isInDocument;
        },
      );

      window.ODS.instances[name].forEach((instance) => {
        uninitializedElements.forEach((element, index) => {
          if (element.isSameNode(instance.element)) {
            uninitializedElements.splice(index, 1);
          }
        });
      });
    }
    if (uninitializedElements.length) {
      if (!Array.isArray(window.ODS.instances[name])) {
        window.ODS.instances[name] = [];
      }
      instances[name] = uninitializedElements.map(
        (element) => new module.Module(element),
      );
      window.ODS.instances[name] = [
        ...window.ODS.instances[name],
        ...instances[name],
      ];
    }
  });
  return instances;
}
